import localforage from 'localforage';
import useStore from '../../../store';
import { formatDateForStorage } from '../../../utils/dateUtils';

const STORAGE_KEY = 'tickets_v2';

// The ticket data is held as a static JSON file. If the data is not present in the local store
// (on the first run, or if the data has been reset), it is reloaded from the file. This means
// that to change the data, you may need to both change the static data file AND run the "Clear 
// Local Data" page under Admin => Advanced settings.
// The static data contains dates which will, over time, become ever more out-of-date. To
// address this, when loading the defaults we adjust the dates to be recent relative to
// the current date. However, we do this in a way that preserves the relative differences
// between the dates for each ticket, so that, for instance, if a ticket was created 10 days
// before it was completed in the static data, it will still be 10 days before completion
// in the adjusted data - it's just that both dates will be moved to be recent.
const MAINTENANCE_TICKET_BASELINE_DATE = new Date('2025-11-11T17:27:00');
const MAINTENANCE_TICKET_FIELDS = [
    'lastActivityDate',
    'firstResponseDateTime',
    'firstResponseDueDateTime',
    'createDate',
    'dueDateTime',
    'completedDate'
];

const toDate = (v) => {
    try {
        if (!v) return null;
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    } catch (e) {
        return null;
    }
};

const format = (d) => (d instanceof Date ? formatDateForStorage(d) : d);

const randPastDate = (maxDays) => {
    const now = new Date();
    const maxMs = maxDays * 24 * 60 * 60 * 1000;
    const delta = Math.floor(Math.random() * maxMs);
    return new Date(now.getTime() - delta);
};

// Applies date adjustments to a single ticket so all dates appear current.
// Maintenance tickets shift dates proportionally from a fixed baseline.
// Normal tickets randomise the baseline date within a recent window and adjust
// all related dates by the same offset to preserve relative gaps.
export const adjustTicketDates = (ticket) => {
    if (!ticket) return ticket;
    const now = new Date();
    const t = { ...ticket };
    const isMaintenanceTicket = Number(ticket.ticketCategory) === 132;

    // Maintenance tickets use a slightly different date-adjustment strategy to normal tickets:
    // shift each date field by the offset from a fixed baseline to now.
    if (isMaintenanceTicket) {
        for (const f of MAINTENANCE_TICKET_FIELDS) {
            const old = toDate(ticket[f]);
            if (!old) {
                t[f] = ticket[f] || null;
                continue;
            }
            const diff = old.getTime() - MAINTENANCE_TICKET_BASELINE_DATE.getTime();
            const newDate = new Date(now.getTime() + diff);
            t[f] = format(newDate);
        }
        return t;
    }

    // Only apply the normal-ticket adjustment logic to non-maintenance tickets.
    // The idea here is that we take one date - either completedDate (if complete) or lastActivityDate
    // (if not complete) - and set that to a recent date. We then adjust all the other date properties 
    // relative to that baseline - so, for instance, if createDate was 10 days before the old 
    // completedDate, it  remains 10 days before the new completedDate.
    // The recent date is a randomly generated date within the past 7 days (if complete) or 30 days (if not
    // complete).
    const adjustPropsRelative = (oldBaseline, newBaseline, props) => {
        if (!oldBaseline) return;
        for (const p of props) {
            const oldVal = toDate(ticket[p]);
            if (!oldVal) continue;
            const diff = oldVal.getTime() - oldBaseline.getTime();
            const newVal = new Date(newBaseline.getTime() + diff);
            t[p] = format(newVal);
        }
    };

    const completeStatuses = JSON.parse(process.env.REACT_APP_STATUS_MAPPINGS || '{}')['Complete'] || []
    const statusIsComplete = completeStatuses.includes(Number(t.status));

    // Reset the dates against the baseline as described above.
    if (statusIsComplete) {
        // baseline is completedDate
        const oldBaseline = toDate(ticket.completedDate) || toDate(ticket.lastActivityDate) || null;
        const newBaseline = randPastDate(7);
        // set complete and lastActivity to new baseline
        t.completedDate = format(newBaseline);
        t.lastActivityDate = format(newBaseline);

        // Adjust related properties relative to old baseline
        adjustPropsRelative(oldBaseline, newBaseline, [
            'firstResponseDateTime',
            'firstResponseDueDateTime',
            'createDate',
            'dueDateTime',
        ]);

        // Service level agreement has been met if completedDate is same or earlier than dueDateTime
        const complete = toDate(t.completedDate);
        const due = toDate(t.dueDateTime);
        if (due) {
            t.serviceLevelAgreementHasBeenMet = complete.getTime() <= due.getTime();
        } else {
            t.serviceLevelAgreementHasBeenMet = false;
        }
    } else {
        // Baseline is lastActivityDate
        const oldBaseline = toDate(ticket.lastActivityDate) || null;
        const newBaseline = randPastDate(30);
        t.lastActivityDate = format(newBaseline);

        adjustPropsRelative(oldBaseline, newBaseline, [
            'firstResponseDateTime',
            'firstResponseDueDateTime',
            'createDate',
            'dueDateTime',
        ]);

        t.serviceLevelAgreementHasBeenMet = false;
    }

    return t;
};

const loadDefaults = async () => {
    try {
        const defaults = require('./_local/_ticketData.json') || { "Items": [] };
        return { Items: defaults.Items.map(adjustTicketDates) };
    } catch (e) {
        return { Items: [] };
    }
};

const read = async () => {
    try {
        const raw = await localforage.getItem(STORAGE_KEY);
        if (!raw) {
            const defaults = await loadDefaults();
            await localforage.setItem(STORAGE_KEY, defaults);
            return defaults;
        }
        return raw;
    } catch (e) {
        return { Items: [] };
    }
};

const write = async (data) => {
    await localforage.setItem(STORAGE_KEY, data);
};

// Build a stable key for each ticket so we can compare across files/stores.
// We use id first, then fall back to ticketNumber if needed.
const getTicketIdentity = (ticket) => {
    if (!ticket) return null;

    const rawId = ticket.id ?? ticket.ticketId ?? ticket.ID ?? ticket.ticket_id;
    if (rawId !== undefined && rawId !== null && rawId !== '') {
        const numericId = Number(rawId);
        if (Number.isFinite(numericId)) {
            return `id:${numericId}`;
        }
        return `id:${String(rawId)}`;
    }

    if (ticket.ticketNumber) {
        return `ticketNumber:${String(ticket.ticketNumber)}`;
    }

    return null;
};

const getDefaultTicketIdentitySet = () => {
    const defaults = require('./_local/_ticketData.json') || { Items: [] };
    const defaultItems = Array.isArray(defaults?.Items) ? defaults.Items : [];
    return new Set(defaultItems.map(getTicketIdentity).filter(Boolean));
};

// Imported tickets may still point at contact ids from the backup file.
// Once contacts are restored, this helper rewrites those references so the
// ticket keeps pointing at the matching contact in the current local store.
const remapTicketContactIds = (ticket, contactIdMap) => {
    if (!ticket || !contactIdMap || typeof contactIdMap.get !== 'function') {
        return ticket;
    }

    const remapValue = (value) => {
        if (value === undefined || value === null || value === '') {
            return value;
        }

        const mapped = contactIdMap.get(String(value));
        return mapped !== undefined ? mapped : value;
    };

    const remappedTicket = { ...ticket };
    remappedTicket.contactId = remapValue(remappedTicket.contactId);
    remappedTicket.contactID = remapValue(remappedTicket.contactID);
    remappedTicket.createdByContactID = remapValue(remappedTicket.createdByContactID);
    remappedTicket.createdByContactId = remapValue(remappedTicket.createdByContactId);

    return remappedTicket;
};

// Returns tickets that exist in local browser storage but are not part of the
// static _ticketData defaults bundled with the application.
export const getUserAddedTickets = async () => {
    const data = await read();
    const currentItems = Array.isArray(data?.Items) ? data.Items : [];
    const defaultIdentitySet = getDefaultTicketIdentitySet();

    // Keep only tickets that are not part of the seeded local JSON defaults.
    return currentItems.filter((ticket) => {
        const identity = getTicketIdentity(ticket);
        return identity && !defaultIdentitySet.has(identity);
    });
};

// Restores backed-up tickets into the local store.
// When applyDateAdjustment is true, the same date logic used for seed data is
// applied to each restored ticket before saving.
export const restoreUserAddedTickets = async (tickets, applyDateAdjustment = false, contactIdMap = null) => {
    if (!Array.isArray(tickets)) {
        throw new Error('restoreUserAddedTickets expects an array of tickets.');
    }

    const data = await read();
    const existingItems = Array.isArray(data?.Items) ? data.Items : [];
    // Imported backup ids are intentionally ignored. In local mode ids are generated
    // from the current highest value, so reusing source-file ids can clash with
    // tickets already in the store after resets or multiple restores.
    const existingTicketNumbers = new Set(
        existingItems
            .map((ticket) => ticket?.ticketNumber)
            .filter((ticketNumber) => ticketNumber !== undefined && ticketNumber !== null && ticketNumber !== '')
            .map((ticketNumber) => String(ticketNumber))
    );

    // Imported tickets are always assigned fresh numeric ids so they cannot clash
    // with ids already in the local store.
    let nextGeneratedId = existingItems.reduce((max, ticket) => {
        const rawId = ticket?.id ?? ticket?.ticketId ?? ticket?.ID ?? ticket?.ticket_id;
        const numericId = Number(rawId);
        if (!Number.isFinite(numericId)) {
            return max;
        }

        return numericId > max ? numericId : max;
    }, 0);

    const itemsToAdd = [];
    let skippedCount = 0;
    const ticketIdMap = new Map();

    for (const ticket of tickets) {
        if (!ticket || typeof ticket !== 'object') {
            skippedCount += 1;
            continue;
        }

        const ticketNumber = ticket?.ticketNumber;
        // Ticket number is the stable business identifier, so use it for duplicate detection.
        if (ticketNumber !== undefined && ticketNumber !== null && ticketNumber !== '' && existingTicketNumbers.has(String(ticketNumber))) {
            skippedCount += 1;
            continue;
        }

        // Optional: apply the same date adjustment strategy used by seeded tickets.
        const preparedTicket = applyDateAdjustment ? adjustTicketDates(ticket) : ticket;
        const preparedTicketWithContacts = remapTicketContactIds(preparedTicket, contactIdMap);
        const originalTicketId = ticket?.id ?? ticket?.ticketId ?? ticket?.ID ?? ticket?.ticket_id;
        const originalTicketIdKey = originalTicketId !== undefined && originalTicketId !== null && originalTicketId !== ''
            ? String(originalTicketId)
            : null;

        nextGeneratedId += 1;
        const importedTicket = {
            ...preparedTicketWithContacts,
            id: nextGeneratedId,
        };

        if (Object.prototype.hasOwnProperty.call(importedTicket, 'ticketId')) {
            importedTicket.ticketId = nextGeneratedId;
        }
        if (Object.prototype.hasOwnProperty.call(importedTicket, 'ID')) {
            importedTicket.ID = nextGeneratedId;
        }
        if (Object.prototype.hasOwnProperty.call(importedTicket, 'ticket_id')) {
            importedTicket.ticket_id = nextGeneratedId;
        }

        if (originalTicketIdKey) {
            ticketIdMap.set(originalTicketIdKey, nextGeneratedId);
        }

        itemsToAdd.push(importedTicket);

        if (ticketNumber !== undefined && ticketNumber !== null && ticketNumber !== '') {
            existingTicketNumbers.add(String(ticketNumber));
        }
    }

    data.Items = [...existingItems, ...itemsToAdd];
    await write(data);

    return {
        totalInFile: tickets.length,
        addedCount: itemsToAdd.length,
        skippedCount,
        ticketIdMap,
    };
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
export const resetToDefaults = async () => {
    const defaults = await loadDefaults();
    await write(defaults);
    return defaults;
};

// Applies the date-adjustment logic to the tickets currently in the store without
// clearing any records. Used by the "Reset Local Data Dates" page under Admin => Advanced settings.
// We always use the original JSON as the source for date calculations so that running refreshDates
// multiple times does not compound the offset (which would push maintenance ticket dates into the
// future). User-added tickets (those not present in the original JSON) are left untouched.
export const refreshDates = async () => {
    const data = await read();
    const currentItems = Array.isArray(data?.Items) ? data.Items : [];

    const defaults = require('./_local/_ticketData.json') || { Items: [] };
    const originalById = new Map((defaults.Items || []).map(t => [t.id, t]));

    const refreshed = {
        Items: currentItems.map(ticket => {
            const original = originalById.get(ticket.id);
            return original ? adjustTicketDates(original) : ticket;
        })
    };
    await write(refreshed);
    return refreshed;
};

export const getAllTickets = async (filter, maxRecords, pageNo = 1, order = null) => {
    const data = await read();

    // We might be looking for a normal or udf field, so this helper function
    // abstracts the logic of retrieving the value accordingly.
    const getFieldValue = (item, field, isUdf) => {
        if (!item || !field) return undefined;
        if (!isUdf) return item[field];

        const udfs = Array.isArray(item.userDefinedFields) ? item.userDefinedFields : [];
        const exact = udfs.find((u) => u?.name === field);
        if (exact) return exact.value;

        const normalizedField = String(field).trim().toLowerCase();
        const caseInsensitive = udfs.find((u) => String(u?.name ?? '').trim().toLowerCase() === normalizedField);
        return caseInsensitive?.value;
    };

    // This helper function converts a value to a Date object if possible, 
    // or returns null if not. This allows us to support date comparisons 
    // in filters, even if the underlying data is just strings.
    const toComparableDate = (value) => {
        if (value == null || value === '') return null;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    // This function evaluates whether a given item matches a filter
    // condition. It supports nested 'or' conditions.
    const evaluateCondition = (item, condition) => {
        if (!condition || typeof condition !== 'object') return true;

        if (condition.op === 'or' && Array.isArray(condition.items)) {
            return condition.items.some((child) => evaluateCondition(item, child));
        }

        const fieldValue = getFieldValue(item, condition.field, condition.udf === true);
        const filterValue = condition.value;

        switch (condition.op) {
            case 'eq':
                // eslint-disable-next-line eqeqeq
                return fieldValue == filterValue;
            case 'noteq':
                // eslint-disable-next-line eqeqeq
                return fieldValue != filterValue;
            case 'exist':
                return fieldValue != null;
            case 'notExist':
                return fieldValue == null;
            case 'contains': {
                const left = String(fieldValue ?? '').toLowerCase();
                const right = String(filterValue ?? '').toLowerCase();
                return left.includes(right);
            }
            case 'gt': {
                const leftDate = toComparableDate(fieldValue);
                const rightDate = toComparableDate(filterValue);
                if (leftDate && rightDate) return leftDate.getTime() > rightDate.getTime();
                return Number(fieldValue) > Number(filterValue);
            }
            case 'lt': {
                const leftDate = toComparableDate(fieldValue);
                const rightDate = toComparableDate(filterValue);
                if (leftDate && rightDate) return leftDate.getTime() < rightDate.getTime();
                return Number(fieldValue) < Number(filterValue);
            }
            default:
                return true;
        }
    };

    // Sort items before pagination. When sorting by ticketNumber (or default),
    // order by numeric id behind the scenes so "Upcoming" tickets follow chronological creation order.
    let sortedItems = Array.isArray(data.Items) ? [...data.Items] : [];
    const parsedOrder = order
        ? (Array.isArray(order) ? order : (typeof order === 'string' ? JSON.parse(order) : null))
        : null;
    const orderClause = Array.isArray(parsedOrder) && parsedOrder.length > 0 ? parsedOrder[0] : null;

    if (orderClause) {
        const field = (orderClause.field === 'ticketNumber' || orderClause.field === 'id') ? 'id' : orderClause.field;
        const isAsc = orderClause.direction === 'asc' || orderClause.direction === 'ascending';
        sortedItems.sort((a, b) => {
            if (field === 'id') {
                const aId = Number(a?.id ?? a?.ticketId ?? a?.ID ?? 0);
                const bId = Number(b?.id ?? b?.ticketId ?? b?.ID ?? 0);
                return isAsc ? aId - bId : bId - aId;
            }
            const aVal = String(a?.[field] ?? '');
            const bVal = String(b?.[field] ?? '');
            return isAsc ? aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
                         : bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' });
        });
    } else {
        sortedItems.sort((a, b) => {
            const aId = Number(a?.id ?? a?.ticketId ?? a?.ID ?? 0);
            const bId = Number(b?.id ?? b?.ticketId ?? b?.ID ?? 0);
            return bId - aId;
        });
    }

    // Apply filters before pagination so paging reflects the filtered dataset.
    if (filter && sortedItems) {
        const filters = JSON.parse(filter);
        sortedItems = sortedItems.filter((item) =>
            Array.isArray(filters)
                ? filters.every((f) => evaluateCondition(item, f))
                : evaluateCondition(item, filters)
        );
    }

    // Count total records and calculate total pages
    const totalRecords = sortedItems.length;

    // If maxRecords is omitted, default it to the total number of tickets in the store.
    const requestedMaxRecords = maxRecords === undefined ? totalRecords : Number(maxRecords);
    const normalizedMaxRecords = Number.isFinite(requestedMaxRecords) ? requestedMaxRecords : totalRecords;
    const maxRecs = totalRecords === 0 ? 0 : Math.max(1, Math.floor(normalizedMaxRecords));
    const page = Math.max(1, Math.floor(Number(pageNo) || 1));

    const totalPages = totalRecords === 0 ? 0 : Math.ceil(totalRecords / maxRecs);
    
    // Calculate start and end indices for this page
    const startIndex = (page - 1) * maxRecs;
    const endIndex = Math.min(startIndex + maxRecs, totalRecords);
    
    // Extract items for this page
    let items = [];
    if (page <= totalPages && totalPages > 0) {
        items = sortedItems.slice(startIndex, endIndex);
    }
    
    const count = items.length;
    
    // Build PrevPageUrl: null for first page, "pageNo={n}" for others
    let prevPageUrl = null;
    if (page > 1) {
        prevPageUrl = `pageNo=${page - 1}`;
    }
    
    // Build NextPageUrl: null for final page or if only one page, "pageNo={n}" otherwise
    let nextPageUrl = null;
    if (page < totalPages) {
        nextPageUrl = `pageNo=${page + 1}`;
    }
    
    return {
        Items: items,
        PageDetails: {
            Count: totalRecords,
            RequestCount: maxRecs,
            PrevPageUrl: prevPageUrl,
            NextPageUrl: nextPageUrl
        }
    };
};

// This adds a ticket to the store. It is used by the New Ticket wizard.
export const addTicket = async (ticket) => {
    const data = await read();
    data.Items.push(ticket);
    await write(data);
    return ticket;
};

// If we're not running locally, we get the new ticket ID from Autotask, but
// in local mode, we need to simulate this by getting the highest ticket ID 
// presently in the store, and incrementing it by 1.
export const getNextTicketId = async () => {
    try {
        const item = await read();
        if (!item || !item.Items || item.Items.length === 0) return 0;
        let max = -Infinity;
        for (const t of item.Items) {
            if (!t) continue;
            const rawId = t.id ?? t.ticketId ?? t.ID ?? t.ticket_id;
            const num = Number(rawId);
            if (!Number.isNaN(num) && isFinite(num)) {
                if (num > max) max = num;
            }
        }
        if (max === -Infinity) return 0;
        return max + 1;
    } catch (e) {
        return 0;
    }
};

// Generate a formatted ticket ID: T<YYYYMMDD>.<0001 to 9999>
// Example: T20231024.0570
// We do this by scanning existing tickets for ones created today, finding the highest sequence number
// used so far, then incrementing that by 1. This means that we are limited to creating 9999 tickets 
// per day in local mode, but if we exceed that, the demo has been going on altogether too long.
export const generateTicketNumber = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    const datePrefix = `T${dateStr}.`;

    // Scan existing tickets for ones matching today's date and extract numeric sequence
    const data = await read();
    let maxSequence = 0;

    if (data && Array.isArray(data.Items)) {
        const re = new RegExp(`^T${dateStr}\\.(\\d{1,})$`);
        for (const ticket of data.Items) {
            if (!ticket || !ticket.ticketNumber) continue;
            const m = String(ticket.ticketNumber).match(re);
            if (m && m[1]) {
                const sequence = Number(m[1]);
                if (!Number.isNaN(sequence) && sequence > maxSequence) {
                    maxSequence = sequence;
                }
            }
        }
    }

    // Next sequence: start at 1 if none found
    const nextSequence = maxSequence + 1;
    // The number should be padded with leading zeros
    const sequenceStr = String(nextSequence).padStart(4, '0');

    return `${datePrefix}${sequenceStr}`;
};

export default {
    getAllTickets,
    addTicket,
    getNextTicketId,
    generateTicketNumber,
    getUserAddedTickets,
    restoreUserAddedTickets,
    resetToDefaults,
    refreshDates,
};
